import { Alert, Badge, Box, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import { getMyFleetVessels } from '../../api/my-fleet';
import { getProAlerts } from '../../api/pro-alert';
import LoadingPanel from '../../common-components/loading-panel/loading-panel';
import PanelTitle from '../../common-components/panel/panel-title';
import TabPanel from '../../common-components/tabs/TabPanel';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useAccessControl from '../../hooks/access-control/useAccessControl';
import {
  setMyFleetVessels,
  setMyFleetVesselsError,
} from '../../state/my-fleet/my-fleet-vessels.slice';
import {
  setProAlerts,
  setProAlertsError,
} from '../../state/pro-alerts/pro-alerts.slice';
import MyFleetAddVesselsContainer from './my-fleet-add-vessels/my-fleet-add-vessels-container';
import MyFleetVesselsList from './my-fleet-vessel-list/my-fleet-vessel-list';

function MyFleetPanel() {
  const dispatch = useAppDispatch();
  const { canAccessFeatureProFeatureBadge } = useAccessControl();
  const { myFleetVesselsError, myFleetVessels, searchFilterValue } =
    useAppSelector((state) => state.myFleetVessels);
  const { proAlertsError } = useAppSelector((state) => state.proAlerts);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const onTabSelect = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchMyFleetVessels = async () => {
    try {
      const vessels = await getMyFleetVessels();
      if (vessels && Array.isArray(vessels) && vessels.length > 0) {
        const sortedVessels = vessels.sort((a, b) =>
          (a?.name ?? '').localeCompare(b?.name ?? '')
        );
        dispatch(setMyFleetVessels(sortedVessels));
      }
    } catch (error: any) {
      dispatch(setMyFleetVesselsError(`${error.message}`));
    }
  };

  const fetchProAlerts = async () => {
    try {
      const proAlerts = await getProAlerts();
      if (
        proAlerts.alerts &&
        Array.isArray(proAlerts.alerts) &&
        proAlerts.alerts.length > 0
      ) {
        dispatch(setProAlerts(proAlerts.alerts));
      }
    } catch (error: any) {
      dispatch(setProAlertsError(`${error.message}`));
    }
  };

  const handleAddSuccess = async () => {
    setLoading(true);
    setTabIndex(0);
    dispatch(setMyFleetVessels(null));
    dispatch(setProAlerts(null));
    await Promise.all([fetchProAlerts(), fetchMyFleetVessels()]);
    setLoading(false);
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([fetchProAlerts(), fetchMyFleetVessels()]).finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        scrollbarWidth: 'thin',
        height: '100%',
      }}
    >
      <PanelTitle>
        {canAccessFeatureProFeatureBadge.enabled ? (
          <Badge
            color="default"
            badgeContent={canAccessFeatureProFeatureBadge.content ?? null}
            sx={{
              '& .MuiBadge-badge': {
                right: -24,
                top: 6,
                fontSize: '0.7rem',
              },
            }}
            data-testid="pro-report-beta-badge"
          >
            My Fleet
          </Badge>
        ) : (
          'My Fleet'
        )}
      </PanelTitle>
      <Tabs value={tabIndex} onChange={onTabSelect} variant="fullWidth">
        <Tab
          label="Saved Vessels"
          id="my-fleet-tab-saved-vessels"
          aria-controls="my-fleet-tab-saved-vessels"
          data-testid="saved-vessels-tab"
          aria-label="Saved Vessels"
          value={0}
        />
        <Tab
          label="Add Vessels"
          id="my-fleet-tab-add-vessels"
          aria-controls="my-fleet-tab-add-vessels"
          data-testid="add-vessels-tab"
          aria-label="Add Vessels"
          value={1}
        />
      </Tabs>
      <TabPanel
        index={tabIndex}
        value={0}
        aria-labelledby="my-fleet-tab-panel-saved-vessels"
      >
        {loading && !myFleetVesselsError && (
          <Box sx={{ paddingTop: '10%' }}>
            <LoadingPanel />
          </Box>
        )}
        {!loading && myFleetVesselsError && (
          <Alert data-testid="error-panel" severity="error">
            Error: Unable to fetch My Fleet data
          </Alert>
        )}
        {!loading && !myFleetVesselsError && proAlertsError && (
          <Alert data-testid="error-panel" severity="error">
            Error: Unable to fetch PRO alert data
          </Alert>
        )}
        {!loading && !myFleetVesselsError && !proAlertsError && (
          <MyFleetVesselsList
            myFleetVessels={myFleetVessels}
            searchFilterValue={searchFilterValue}
            onDeleteSuccess={handleAddSuccess}
          />
        )}
      </TabPanel>
      <TabPanel
        index={tabIndex}
        value={1}
        aria-labelledby="my-fleet-tab-panel-add-vessels"
      >
        <MyFleetAddVesselsContainer
          onAddSuccess={handleAddSuccess}
          myFleetVessels={myFleetVessels}
        />
      </TabPanel>
    </Box>
  );
}

export default MyFleetPanel;
